import { useCallback, useEffect, useState } from "react";

// Tilt reading permission status
const isIOS =
  typeof navigator !== "undefined" &&
  (/iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));

// Only iOS 13+ makes the page ask for motion access, and the permission
// function alone doesn't tell us that: some Android browsers define it too.
const needsPermission =
  isIOS &&
  typeof window != "undefined" &&
  typeof window.DeviceOrientationEvent !== "undefined" &&
  typeof window.DeviceOrientationEvent.requestPermission === "function";

// Remember the permission granted status throughout the page lifecycle.
let motionGrantedThisPage = false;

// Device detection hook
//
// - Returns {
//     tilt,              => The tilt value (beta only)
//     supported,         => Whether the environment supports angle detection
//     permission,        => Permission to read tilt value (iOS only)
//     requestPermission  => Function to request permission (iOS only)
//   }
export const useDeviceTilt = ({ active = true } = {}) => {
  // Tilt supported (e.g., desktop doesn't support tilt)
  const [supported, setSupported] = useState(true);

  // Permission to read tilt value (for iOS devices)
  // Values: "granted", "denied", or "prompt" (not asked yet)
  const [motionPermission, setMotionPermission] = useState(
    needsPermission && !motionGrantedThisPage ? "prompt" : "granted",
  );

  // Permission prompt triggered by user-gesture.
  // On iOS it shows the system dialog.
  // Elsewhere there is nothing to ask, so it resolves "granted" right away.
  const requestMotionPermission = useCallback(async () => {
    if (!needsPermission) {
      // Not iOS device; resolve to "granted".
      setMotionPermission("granted");
      motionGrantedThisPage = true;
      return "granted";
    }
    try {
      const result = await window.DeviceOrientationEvent.requestPermission();
      motionGrantedThisPage = result === "granted";
      setMotionPermission(result === "granted" ? "granted" : "denied");
      return result;
    } catch (error) {
      // thrown when not called from a user gesture, or the page isn't HTTPS
      setMotionPermission("denied");
      return "denied";
    }
  }, []);

  // Tilt value
  const [tilt, setTilt] = useState(null);

  // Attempt to read tilt value on launch
  useEffect(() => {
    if (!active || motionPermission !== "granted") return undefined;

    // Some browsers expose the event but never fire it (e.g. desktops).
    // If no usable reading arrives soon, treat it as unsupported.
    let valueRead = false;
    const timeout = setTimeout(() => {
      if (!valueRead) setSupported(false);
    }, 1_000);
    const handleOrientation = (event) => {
      // Look for beta(front-back) values.
      if (event.beta === null) return;
      valueRead = true;
      setSupported(true);

      // Whole degrees only. If the rounded value is the same, returning `prev`
      // makes React skip the re-render, so a steady hand re-renders nothing.
      const next = Math.round(event.beta - 90);
      setTilt((previous) => (previous === next ? previous : next));
    };

    // Add event listener to the deviceorientation event.
    window.addEventListener("deviceorientation", handleOrientation);

    // Cleaner function
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("deviceorientation", handleOrientation);
    };
  }, [active, motionPermission]);

  // Return value
  return { tilt, supported, motionPermission, requestMotionPermission };
};
